import { useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCircle, EnvelopeSimple, Lock, SpinnerGap, ShieldCheck, CheckCircle } from "@phosphor-icons/react";

function Card({ icon: Icon, title, desc, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-1"><Icon size={20} weight="duotone" className="text-[#1F6FE5]" /><h2 className="font-display font-bold text-xl tracking-tight">{title}</h2></div>
      <p className="text-sm text-zinc-500 mb-5">{desc}</p>
      {children}
    </div>
  );
}

function TwoFactorCard() {
  const { user, refresh } = useAuth();
  const enabled = !!user?.totp_enabled;
  const [step, setStep] = useState("idle"); // idle | enrolling | recovery
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [dis, setDis] = useState({ password: "", code: "" });
  const [busy, setBusy] = useState(false);

  const begin = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/enroll"); setUri(data.provisioning_uri); setStep("enrolling"); setCode(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/2fa/verify-enrollment", { code }); setRecovery(data.recovery_codes); setStep("recovery"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/2fa/disable", dis); toast.success("Two-factor authentication disabled"); setDis({ password: "", code: "" }); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 md:col-span-2" data-testid="twofactor-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={20} weight="duotone" className="text-[#1F6FE5]" />
        <h2 className="font-display font-bold text-xl tracking-tight">Two-factor authentication</h2>
        {enabled && <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid="twofa-status-on"><CheckCircle size={12} weight="fill" /> On</span>}
      </div>
      <p className="text-sm text-zinc-500 mb-5">Add a second step at sign-in using an authenticator app, with an email code as backup.</p>

      {step === "recovery" && recovery ? (
        <div data-testid="twofa-recovery">
          <p className="text-sm text-zinc-600 mb-3">Save these single-use recovery codes somewhere safe — they won't be shown again.</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3 p-4 bg-zinc-50 border border-zinc-200 rounded-lg font-mono text-sm">
            {recovery.map((rc) => <div key={rc} className="text-center py-1 tracking-widest">{rc}</div>)}
          </div>
          <div className="flex gap-2">
            <button data-testid="twofa-copy-recovery" onClick={() => { navigator.clipboard.writeText(recovery.join("\n")); toast.success("Copied"); }} className="h-10 px-4 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors">Copy all</button>
            <button data-testid="twofa-recovery-done" onClick={() => { setStep("idle"); setRecovery(null); }} className="h-10 px-4 bg-[#1F6FE5] text-white rounded-md text-sm font-semibold hover:bg-[#1552C9] transition-colors">Done</button>
          </div>
        </div>
      ) : enabled ? (
        <form onSubmit={disable} className="space-y-3 max-w-md" data-testid="twofa-disable-form">
          <p className="text-sm text-zinc-600">To turn it off, confirm your password and an authenticator or recovery code.</p>
          <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Password</Label><Input data-testid="twofa-disable-password" type="password" required value={dis.password} onChange={(e) => setDis({ ...dis, password: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Authenticator or recovery code</Label><Input data-testid="twofa-disable-code" required value={dis.code} onChange={(e) => setDis({ ...dis, code: e.target.value })} className="mt-1.5 font-mono" placeholder="123456 or XXXX-XXXX" /></div>
          <button data-testid="twofa-disable-btn" disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 bg-red-500 text-white rounded-md text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50">{busy && <SpinnerGap size={15} className="animate-spin" />} Disable 2FA</button>
        </form>
      ) : step === "enrolling" ? (
        <div className="flex flex-col sm:flex-row gap-6" data-testid="twofa-enroll">
          <div className="bg-white p-3 border border-zinc-200 rounded-lg shrink-0 self-start"><QRCodeCanvas value={uri} size={168} data-testid="twofa-qr" /></div>
          <form onSubmit={verify} className="space-y-3 flex-1 max-w-xs">
            <p className="text-sm text-zinc-600">Scan the QR in Google Authenticator / Authy, then enter the 6-digit code.</p>
            <Input data-testid="twofa-enroll-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="font-mono tracking-[0.4em] text-center text-lg" />
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep("idle")} className="h-10 px-4 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors">Cancel</button>
              <button data-testid="twofa-verify-btn" disabled={busy || code.length !== 6} className="inline-flex items-center gap-2 h-10 px-4 bg-[#1F6FE5] text-white rounded-md text-sm font-semibold hover:bg-[#1552C9] transition-colors disabled:opacity-50">{busy && <SpinnerGap size={15} className="animate-spin" />} Verify & enable</button>
            </div>
          </form>
        </div>
      ) : (
        <button data-testid="twofa-enable-btn" onClick={begin} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 bg-black text-white rounded-md text-sm font-semibold hover:bg-zinc-800 transition-colors active:scale-95 disabled:opacity-50">{busy && <SpinnerGap size={15} className="animate-spin" />} Set up two-factor</button>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState({ new_email: "", password: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState("");

  const saveName = async (e) => {
    e.preventDefault(); setBusy("name");
    try { await api.patch("/auth/profile", { name }); toast.success("Name updated"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const saveEmail = async (e) => {
    e.preventDefault(); setBusy("email");
    try { await api.post("/auth/change-email", email); toast.success("Email updated"); setEmail({ new_email: "", password: "" }); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const savePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    if (pw.new_password.length < 12) { toast.error("Use at least 12 characters"); return; }
    setBusy("pw");
    try { await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }); toast.success("Password changed"); setPw({ current_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const Btn = ({ id, k, label }) => (
    <button data-testid={id} disabled={busy === k} className="inline-flex items-center gap-2 h-10 px-4 bg-black text-white rounded-md text-sm font-semibold hover:bg-zinc-800 transition-colors active:scale-95 disabled:opacity-50">
      {busy === k && <SpinnerGap size={15} className="animate-spin" />} {label}
    </button>
  );

  return (
    <Layout back="/app">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-1">Your profile</h1>
      <p className="text-zinc-500 mb-8">Manage your personal account details.</p>
      <div className="grid md:grid-cols-2 gap-5 max-w-4xl" data-testid="profile-page">
        <Card icon={UserCircle} title="Name" desc="How you appear across SelectFlow.">
          <form onSubmit={saveName} className="space-y-4">
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Full name</Label><Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
            <Btn id="save-name" k="name" label="Save name" />
          </form>
        </Card>
        <Card icon={EnvelopeSimple} title="Email address" desc={`Current: ${user?.email}`}>
          <form onSubmit={saveEmail} className="space-y-4">
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">New email</Label><Input data-testid="profile-new-email" type="email" required value={email.new_email} onChange={(e) => setEmail({ ...email, new_email: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Confirm with password</Label><Input data-testid="profile-email-password" type="password" required value={email.password} onChange={(e) => setEmail({ ...email, password: e.target.value })} className="mt-1.5" /></div>
            <Btn id="save-email" k="email" label="Update email" />
          </form>
        </Card>
        <Card icon={Lock} title="Password" desc="Use at least 12 characters.">
          <form onSubmit={savePw} className="space-y-4">
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Current password</Label><Input data-testid="profile-current-password" type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">New password</Label><Input data-testid="profile-new-password" type="password" required value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Confirm new password</Label><Input data-testid="profile-confirm-password" type="password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5" /></div>
            <Btn id="save-password" k="pw" label="Change password" />
          </form>
        </Card>
        <TwoFactorCard />
      </div>
    </Layout>
  );
}
